import { useState } from 'react'
import {
  REACTIONS,
  reactToContent,
  reactToComment,
  totalReactions,
  type ReactionCounts,
  type ReactionKind,
} from '../lib/feed'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'

/**
 * The reaction row on a feed post (FED-3, migration 018).
 *
 * ── ONE REACTION, NOT A COLLECTION ──────────────────────────────────────────
 *
 * Slack lets you add every emoji in the picker. This does not, and the limit
 * is in the schema rather than here: content_reaction is unique on
 * (content_id, person_id) with no kind in the key. Picking a second replaces
 * the first; picking the one you already have clears it. That is why every
 * button is a radio in behaviour even though it looks like four toggles, and
 * why aria-pressed is on all four rather than aria-checked on one — they are
 * still four independent controls to a screen reader, each of which happens to
 * turn the others off.
 *
 * ── WHY THE COUNT IS NOT RENDERED AS ZERO ───────────────────────────────────
 *
 * A kind nobody has picked has no key in the counts object, and the button
 * shows a glyph and a label with no number beside it. "0" is a claim that the
 * company looked and declined; nothing is the truth, which is that nobody has
 * pressed it. Same reasoning that kept view counts and comment counts off this
 * card in the first place.
 *
 * ── WHY THE OPTIMISTIC UPDATE IS NOT ROLLED BACK BY HAND ────────────────────
 *
 * react_to_content returns the authoritative counts and the viewer's own
 * reaction, so the success path REPLACES local state with the server's answer
 * rather than trusting the guess. The failure path restores the snapshot taken
 * before the click. Neither branch recomputes anything, which matters because
 * the toggle-to-clear rule lives in the database function — a client that
 * modelled it locally would be a second implementation, and it would drift the
 * first time somebody double-tapped on a slow connection.
 *
 * ── ONE COMPONENT, TWO TARGETS (058, 12 Sep) ────────────────────────────────
 *
 * David asked for reactions on replies. This takes a `target` — a content id or
 * a comment id — and calls the matching RPC. A second `CommentReactionBar`
 * would have been quicker and is the `content_creator` mistake in component
 * form: every rule above (one reaction not a collection, absent rather than
 * zero, the server's answer overwrites the guess, the failure says so) would
 * then exist twice and diverge on the first edit to either.
 *
 * `compact` is presentation and nothing else — smaller buttons, no running
 * total line — because a reply is a smaller thing than a post and a "3
 * reactions" summary under a one-line reply is longer than the reply.
 */
export type ReactionTarget = { contentId: string } | { commentId: string }

export function ReactionBar({
  target,
  counts,
  mine,
  compact = false,
}: {
  target: ReactionTarget
  counts: ReactionCounts
  mine: ReactionKind | null
  compact?: boolean
}) {
  const h = HOME[useHubLang()]
  const [state, setState] = useState<{ counts: ReactionCounts; mine: ReactionKind | null }>({
    counts,
    mine,
  })
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  async function press(kind: ReactionKind) {
    if (busy) return
    const before = state
    setBusy(true)
    setFailed(false)

    // Optimistic, so the button responds on a slow connection. The guess only
    // has to be close; the server's answer overwrites it either way.
    const clearing = before.mine === kind
    const next: ReactionCounts = { ...before.counts }
    if (before.mine) next[before.mine] = Math.max(0, (next[before.mine] ?? 1) - 1)
    if (!clearing) next[kind] = (next[kind] ?? 0) + 1
    for (const k of Object.keys(next) as ReactionKind[]) if (!next[k]) delete next[k]
    setState({ counts: next, mine: clearing ? null : kind })

    try {
      const pick = clearing ? null : kind
      const res =
        'contentId' in target
          ? await reactToContent(target.contentId, pick)
          : await reactToComment(target.commentId, pick)
      setState({ counts: res.counts, mine: res.mine })
    } catch {
      // Put it back exactly as it was. Saying so matters more than it looks:
      // a reaction that silently fails to stick reads as the hub being broken,
      // and the person will press it four more times.
      setState(before)
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const total = totalReactions(state.counts)

  return (
    <div className={`reactions${compact ? ' reactions--compact' : ''}`}>
      <ul className="reactions__list">
        {REACTIONS.map((r) => {
          const n = state.counts[r.kind]
          const active = state.mine === r.kind
          return (
            <li key={r.kind}>
              <button
                type="button"
                className={`reaction${active ? ' reaction--on' : ''}${compact ? ' reaction--sm' : ''}`}
                onClick={() => press(r.kind)}
                aria-pressed={active}
                aria-label={n ? h.reactionCount(h.reactions[r.kind], n) : h.reactions[r.kind]}
                title={h.reactions[r.kind]}
                disabled={busy}
              >
                <span className="reaction__glyph" aria-hidden="true">
                  {r.glyph}
                </span>
                {/* Absent, not zero. See the header. */}
                {n ? <span className="reaction__n">{n}</span> : null}
              </button>
            </li>
          )
        })}
      </ul>

      {/* Suppressed in a thread: the per-button counts are already there, and
          a summary line under a one-line reply is longer than the reply. */}
      {total > 0 && !compact && (
        <p className="reactions__total">
          {h.totalReactions(total)}
        </p>
      )}

      {failed && (
        <p className="reactions__error" role="status">
          {h.reactionFailed}
        </p>
      )}
    </div>
  )
}
